import React, { useEffect, useMemo, useState } from 'react';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { WEBFORM_TYPES } from '../../../config/constants';
import SwirlIllustrations from '../../SwirlIllustrations';
import WebformAssessmentWrapper from '../Assessment/Wrapper';
import {
  QuizContext,
  type QuizQuestion,
  type QuizContextProps,
} from '../../../context/QuizContext';

import type {
  CustomImmutableEntity,
  WebformBase,
  WebformQuizQuestion,
  WebformState,
} from '../types';

import './style.scss';

// Subcomponents.
import Actions from './Actions';
import Count from '../Assessment/Count';
import Header from '../Assessment/Header';
import QuestionInfo from './QuestionInfo';
import Submission from './Submission';
import Thumb from './Thumb';

interface Subcomponents {
  Actions: typeof Actions;
  Count: typeof Count;
  Header: typeof Header;
  QuestionInfo: typeof QuestionInfo;
  Submission: typeof Submission;
  Thumb: typeof Thumb;
}

type QuizPagesReturnType = Record<string, WebformBase>;

interface WebformQuizChildrenProps {
  setShowSubmission: (value: boolean) => void;
}

export interface WebformQuizProps {
  quizId: string;
  children: React.ReactNode | ((props: WebformQuizChildrenProps) => React.ReactNode);
}

function getQuizPages(wizardPages: QuizPagesReturnType) {
  return Object.entries(wizardPages).map(([key, page]) => ({
    id: key,
    title: page['#title'],
  }));
}

function getQuizQuestions(wizardPages: QuizPagesReturnType) {
  const quizQuestions: QuizQuestion[] = [];
  const quizPages = getQuizPages(wizardPages);

  quizPages.forEach(({ id }) => {
    const fields = wizardPages[id];

    if (!fields) {
      return;
    }

    // In webform, all keys that don't start with '#', are children of the wizard page.
    const questionsKeys = Object.keys(fields).filter((key) => key.indexOf('#') !== 0);

    questionsKeys.forEach((key) => {
      const question = fields[key] as WebformQuizQuestion;
      const type = question['#type'];

      quizQuestions.push({
        id: question['#webform_key'],
        type,
        parentId: question['#webform_parent_key'],
        title: question['#title'],
        description: question['#description_correct_answer'],
        readMore: question['#body_read_more'],
        options: question['#options'],
        currentPage: 'question',
        showAnswerOnNewPage: question['#type'] === 'quiz_radios' ? question['#show_answer_on_new_page'] : true,
        correctAnswers: question['#correct_answers']?.split(',').map((item) => item.trim()) || [],
        givenAnswers: [],
        isCorrect: undefined,
        ...(type === 'quiz_radios_with_image' && {
          images: question['#images'],
        }),
      });
    });
  });

  return quizQuestions;
}

const WebformQuiz: React.FC<WebformQuizProps> & Subcomponents = ({ quizId, children }) => {
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuizQuestionId, setCurrentQuizQuestionId] = useState('');
  const [showSubmission, setShowSubmission] = useState(false);
  const [wizardPagesHaveBeenLoaded, setWizardPagesHaveBeenLoaded] = useState(false);

  const wizardPages = useSelector<WebformState, CustomImmutableEntity<QuizPagesReturnType>>(
    // @ts-expect-error
    (state) => {
      const fields = state.getIn(['webform', quizId, 'fields'], Immutable.Map());
      return fields.filter((field) => field.get('#type') === WEBFORM_TYPES.WEBFORM_WIZARD_PAGE);
    },
  );

  useEffect(() => {
    if (wizardPagesHaveBeenLoaded || wizardPages.size === 0) {
      return;
    }

    setQuizQuestions(getQuizQuestions(wizardPages.toJS()));
    setWizardPagesHaveBeenLoaded(true);
  }, [wizardPages, wizardPagesHaveBeenLoaded]);

  const quizContext = useMemo((): QuizContextProps => ({
    quizId,
    quizPages: getQuizPages(wizardPages.toJS()),
    quizQuestions,
    currentQuizQuestion: quizQuestions.find((question) => question.id === currentQuizQuestionId),
    quizScore: {
      total: quizQuestions.length,
      correct: quizQuestions.filter((question) => question.isCorrect).length,
    },
    updateCurrentQuizQuestionId: setCurrentQuizQuestionId,
    updateQuizQuestion: (newQuestion) => {
      setQuizQuestions((currentQuestions) => {
        const currentQuestionIndex = currentQuestions.findIndex((question) => (
          question.id === newQuestion.id
        ));

        if (!currentQuestions[currentQuestionIndex]) {
          return currentQuestions;
        }

        const updatedQuestion = {
          ...currentQuestions[currentQuestionIndex],
          ...newQuestion,
        } as QuizQuestion;

        return [
          ...currentQuestions.slice(0, currentQuestionIndex),
          updatedQuestion,
          ...currentQuestions.slice(currentQuestionIndex + 1),
        ];
      });
    },
    resetQuiz: () => {
      setQuizQuestions(getQuizQuestions(wizardPages.toJS()));
      setShowSubmission(false);
    },
  }), [currentQuizQuestionId, quizId, quizQuestions, wizardPages]);

  return (
    <QuizContext.Provider value={quizContext}>
      <WebformAssessmentWrapper id={quizId} className="WebformQuiz">
        {!showSubmission && (
          <Header />
        )}

        <main>
          {showSubmission ? (
            <Submission />
          ) : (
            <div className="WebformQuiz__content">
              <Container>
                <div className="row">
                  <div className="col-lg-10 offset-lg-1">
                    {typeof children === 'function' ? children({ setShowSubmission }) : children}
                  </div>
                </div>
              </Container>
              <SwirlIllustrations variant={5} />
            </div>
          )}
        </main>
      </WebformAssessmentWrapper>
    </QuizContext.Provider>
  );
};

WebformQuiz.Actions = Actions;
WebformQuiz.Count = Count;
WebformQuiz.Header = Header;
WebformQuiz.QuestionInfo = QuestionInfo;
WebformQuiz.Submission = Submission;
WebformQuiz.Thumb = Thumb;

export default WebformQuiz;
